import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import capitalize from 'lodash/capitalize';

import { createIconComponent } from 'uikit/icons';

import { useUrlSearch } from '@/search';

import styles from '../styles/tabs.module.scss';

const Tabs = ({
  activeId,
  className = '',
  bodyClassName = '',
  navClassName = '',
  isVertical = false,
  onClick = () => {},
  queryParamsToPersist = [],
  shouldRenderAll = false,
  tabs = [],
  url = '',
  variant = 'admin',
}) => {
  const directionClassName = isVertical ? styles.isVertical : styles.isHorizontal;
  const { pathname } = useLocation();
  const urlSearch = useUrlSearch();

  const params = queryParamsToPersist.reduce((acc, key) => {
    const value = urlSearch.getValue(key);
    return { ...acc, ...(value && { [key]: value }) };
  }, {});

  const search = urlSearch.getParams(params).toString();

  return (
    <div
      className={classnames([
        styles.element,
        styles[`isVariant${capitalize(variant)}`],
        directionClassName,
        className,
      ])}
    >
      <nav className={classnames(styles.nav, directionClassName, navClassName)}>
        <ul className={classnames(styles.list, directionClassName)}>
          {tabs.map(({ icon, id, title }) => (
            <li className={classnames(styles.title, directionClassName)} key={id}>
              {typeof title === 'string' ? (
                <Link
                  className={classnames(styles.link, directionClassName, {
                    [styles.isActive]: id === activeId,
                  })}
                  data-object-name={`${title} Tab`}
                  onClick={() => onClick(id)}
                  to={
                    url
                      ? { pathname: `${url}/${id}`, search }
                      : { hash: String(id), pathname, search }
                  }
                >
                  {title}
                  {icon && isVertical && createIconComponent({ icon })}
                </Link>
              ) : (
                title
              )}
            </li>
          ))}
        </ul>
      </nav>
      {tabs.flatMap(({ id, body }) => {
        const isActiveTab = id === activeId;

        if (!isActiveTab && !shouldRenderAll) return [];

        return (
          <div
            className={classnames(styles.body, directionClassName, bodyClassName, {
              [styles.isActive]: isActiveTab,
            })}
            key={id}
          >
            {body}
          </div>
        );
      })}
    </div>
  );
};

Tabs.propTypes = {
  activeId: PropTypes.string.isRequired,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  navClassName: PropTypes.string,
  isVertical: PropTypes.bool,
  onClick: PropTypes.func,
  queryParamsToPersist: PropTypes.arrayOf(PropTypes.string),
  shouldRenderAll: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      body: PropTypes.node,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    }),
  ),
  url: PropTypes.string,
  variant: PropTypes.oneOf(['admin', 'student']),
};

export default React.memo(Tabs);
